import * as Accordion from '@radix-ui/react-accordion'
import { CheckoutProvider, useCheckoutContext } from '@haus-storefront-react/checkout'
import type { Order } from '@haus-storefront-react/shared-types'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { useActiveOrder } from '@haus-storefront-react/hooks'
import { Typography } from '@components'
import { useTranslation } from 'react-i18next'
import CustomerDetails from './steps/customer-details/CustomerDetails'
import Payment from './steps/payment/Payment'
import styles from './Checkout.module.scss'
import type { CheckoutData, PortalContainer } from './types'

const CheckoutSteps = ({
  activeOrder,
  portalContainer,
}: {
  activeOrder: Order
  portalContainer?: PortalContainer
}) => {
  const { t } = useTranslation()
  const { currentStep, setCurrentStep } = useCheckoutContext<CheckoutData>()
  const steps = [
    {
      component: CustomerDetails,
      type: 'customerDetails',
      title: t('checkout.steps.customerDetails'),
    },
    { component: Payment, type: 'paymentMethod', title: t('checkout.steps.payment') },
  ]

  const handleStepClick = (index: number) => {
    if (index > currentStep) return
    setCurrentStep(index)
  }

  return (
    <Accordion.Root type="multiple" value={[String(currentStep)]} className={styles.accordionRoot}>
      {steps.map((step, index) => (
        <Accordion.Item
          key={index}
          value={String(index)}
          disabled={index > currentStep}
          className={styles.accordionItem}
        >
          <Accordion.Header className={styles.accordionHeader}>
            <Accordion.Trigger
              className={styles.accordionTrigger}
              onClick={() => handleStepClick(index)}
            >
              <Typography variant="XL" as="span">
                {index + 1}. {step.title}
              </Typography>
              <ChevronDownIcon className={styles.accordionChevron} aria-hidden />
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className={styles.accordionContent}>
            {index === currentStep && (
              <step.component
                index={index}
                activeOrder={activeOrder}
                portalContainer={portalContainer}
              />
            )}
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  )
}

interface CheckoutWidgetProps {
  portalContainer?: PortalContainer
}

const CheckoutWidget = ({ portalContainer }: CheckoutWidgetProps) => {
  const { data: activeOrder } = useActiveOrder()

  if (!activeOrder) {
    return null
  }

  return (
    <div>
      <CheckoutProvider>
        <CheckoutSteps activeOrder={activeOrder} portalContainer={portalContainer} />
      </CheckoutProvider>
    </div>
  )
}

export default CheckoutWidget
